import {
  Autocomplete,
  createFilterOptions,
  debounce,
  FormControl,
  Grid,
  List,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch } from "../../../app/hooks";
import { CustomerOrgCompany } from "../../../common/types/apiTypes";
import {
  CustomerContract,
  customerOrgAndCompAutocomplete,
} from "../../customer/customerAPI";
import { EezyCompany, TYPE_EXTERNAL } from "../invoiceAPI";
import { fetchCustomerOrgContracts } from "../invoiceDetailedSlice";
import { leadingZerosDateFormat } from "../../../common/helpers/formatDateTime";
import { emptyCustomerContract } from "../../customer/contract/helpers/contractHelpers";

interface Props {
  invoiceType: string;
  customer: CustomerOrgCompany;
  selectCustomer: (customer: CustomerOrgCompany | null) => void;
  customerContract: CustomerContract;
  selectContract: (contract: CustomerContract) => void;
  valueDate: Date | null;
  selectValueDate: (date: Date | null) => void;
  eezyBuyerOptions: EezyCompany[];
  eezyBuyer: EezyCompany;
  selectEezyBuyer: (buyer: EezyCompany) => void;
  sellerId: string;
}

const initCustOrgOptions = [{ id: "", name: "", customerOrganizations: [] }];

export const InvoiceDialogAdditionalInfo = ({
  invoiceType,
  customer,
  selectCustomer,
  customerContract: cContract,
  selectContract,
  valueDate: vDate,
  selectValueDate: selectVDate,
  eezyBuyerOptions,
  eezyBuyer,
  selectEezyBuyer: selectEBuyer,
  sellerId,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("invoices");
  const [type, setType] = useState(invoiceType);

  const [customerSearchString, setCustomerSearchString] = useState("");
  const [customerOrgOptions, setCustomerOrgOptions] =
    useState<CustomerOrgCompany[]>(initCustOrgOptions);
  const [customerContractOptions, setCustomerContractOptions] = useState<
    CustomerContract[]
  >([]);

  const [eezyCompany, setEezyCompany] = useState<EezyCompany>(eezyBuyer);
  const [customerOrgCompany, setCustomerOrgCompany] =
    useState<CustomerOrgCompany | null>(customer);
  const [customerContract, setCustomerContract] = useState<any>(cContract);
  const [valueDate, setValueDate] = useState<Date | null>(vDate);

  const selectCustomerOrg = (
    event: any,
    newValue: CustomerOrgCompany | null,
  ) => {
    setCustomerOrgCompany(newValue);
    selectCustomer(newValue);
  };

  const selectCustomerContract = (event: any) => {
    const contractId = event.target.value;
    const value = customerContractOptions.filter(
      (option) => option.id === contractId,
    );
    setCustomerContract(value[0].id);
    selectContract(value[0]);
  };

  const selectValueDate = (newDate: Date | null) => {
    setValueDate(newDate);
    selectVDate(newDate);
  };

  const selectEezyBuyer = (event: any, newValue: any) => {
    if (!!newValue) {
      setEezyCompany(newValue);
      selectEBuyer(newValue);
    }
  };

  const handleCustomerSearchStringChange = debounce(
    (value: string) => setCustomerSearchString(value),
    500,
  );

  const dispatchCustomerOrgSearch = (searchString: string) => {
    try {
      customerOrgAndCompAutocomplete({ name: searchString }).then((res) => {
        setCustomerOrgOptions(res);
      });
    } catch (e: any) {
      console.error("Failed to fetch CustomerOrganizations: ", e);
    }
  };

  const customerOptions = useMemo(() => {
    return customerOrgOptions.flatMap((item) =>
      item.customerOrganizations.map((org) => ({
        id: item.id,
        name: item.name,
        customerOrganizations: [{ id: org.id, name: org.name }],
      })),
    );
  }, [customerOrgOptions]);

  useEffect(() => {
    if (customerSearchString !== "") {
      dispatchCustomerOrgSearch(customerSearchString);
    }
  }, [customerSearchString]);

  useEffect(() => {
    if (!!customerOrgCompany && customerOrgCompany.id !== "") {
      dispatch(
        fetchCustomerOrgContracts(
          customerOrgCompany.customerOrganizations[0].id,
        ),
      ).then((action) => {
        if (isFulfilled(action)) {
          const contracts = action.payload.filter(
            (contract) => contract.eezyCompany.id === sellerId,
          );
          setCustomerContractOptions(contracts);
          if (contracts.length === 1) {
            setCustomerContract(contracts[0].id);
            selectContract(contracts[0]);
          }
        }
      });
    }
  }, [customerOrgCompany, selectContract, sellerId, dispatch]);

  useEffect(() => {
    setType(invoiceType);
  }, [invoiceType]);

  useEffect(() => {
    setCustomerContract(emptyCustomerContract);
  }, [customer]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography
          variant="header"
          sx={{ textTransform: "uppercase", fontWeight: 700 }}
        >
          {t("additionalInfo")}
        </Typography>
      </Grid>
      {type === TYPE_EXTERNAL ? (
        <>
          <Grid item container xs={12}>
            <Grid item xs={4} sx={{ display: "flex" }}>
              <Typography
                variant="secondary"
                align="right"
                sx={{ width: "100%", marginRight: "10px" }}
              >
                {t("billedCustomer")}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <FormControl fullWidth>
                <Autocomplete
                  value={customerOrgCompany}
                  sx={{
                    backgroundColor: "rgba(249, 249, 250, 0.6)",
                  }}
                  options={customerOptions}
                  getOptionLabel={(option) => {
                    const custOrg = option.customerOrganizations[0];
                    return custOrg ? custOrg.name : "";
                  }}
                  filterOptions={createFilterOptions({
                    stringify: (option) => JSON.stringify(option),
                  })}
                  groupBy={(option: any) => option.name}
                  renderGroup={({ group, children }) => (
                    <>
                      <ListSubheader
                        disableSticky
                        sx={{
                          color: "#9167CA",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          lineHeight: "20px",
                        }}
                      >
                        <Typography variant="header">{group}</Typography>
                      </ListSubheader>
                      <List sx={{ paddingLeft: "32px" }}>{children}</List>
                    </>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      name="customerOrgCompany"
                      required
                    />
                  )}
                  onChange={(_, newValue: CustomerOrgCompany | null) =>
                    selectCustomerOrg(_, newValue)
                  }
                  onInputChange={(_, newValue: string) => {
                    if (!!newValue) {
                      handleCustomerSearchStringChange(newValue);
                    }
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid item container xs={12}>
            <Grid item xs={4} sx={{ display: "flex" }}>
              <Typography
                variant="secondary"
                align="right"
                sx={{ width: "100%", marginRight: "10px" }}
              >
                {t("customerContract")}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <FormControl fullWidth>
                <Select
                  size="small"
                  value={customerContract}
                  onChange={selectCustomerContract}
                >
                  {customerContractOptions
                    ? customerContractOptions.map((contract) => (
                        <MenuItem key={contract.id} value={contract.id}>
                          {contract.name}
                        </MenuItem>
                      ))
                    : null}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </>
      ) : (
        <Grid item container xs={12}>
          <Grid item xs={4} sx={{ display: "flex" }}>
            <Typography
              variant="secondary"
              align="right"
              sx={{ width: "100%", marginRight: "10px" }}
            >
              {t("billedEezyCompany")}
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <FormControl fullWidth>
              <Autocomplete
                value={eezyCompany}
                size="small"
                getOptionLabel={(option) => option.name}
                onChange={selectEezyBuyer}
                options={eezyBuyerOptions}
                renderInput={(params: any) => <TextField {...params} />}
              />
            </FormControl>
          </Grid>
        </Grid>
      )}

      <Grid item container xs={12}>
        <Grid item xs={4} sx={{ display: "flex" }}>
          <Typography
            variant="secondary"
            align="right"
            sx={{ width: "100%", marginRight: "10px" }}
          >
            {t("valueDate")}
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <FormControl fullWidth>
            <DesktopDatePicker
              format={leadingZerosDateFormat}
              label={null}
              value={valueDate}
              onChange={(newValue) => {
                selectValueDate(newValue);
              }}
              slotProps={{ textField: { size: "small" } }}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid item container xs={12}>
        <Grid item xs={4} sx={{ display: "flex" }}>
          <Typography
            variant="secondary"
            align="right"
            sx={{ width: "100%", marginRight: "10px" }}
          >
            {t("invoiceTemplate")}
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <FormControl fullWidth>
            <Select disabled size="small" value={""}>
              {/* TODO get invoice template options */}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Grid>
  );
};
